import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { mediaUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import SearchBar from "./SearchBar";
import { NLSearch } from "./NLSearch";

const DURATION = 8000;

function Slide({ s, active, reduced, preload }) {
  const { tr } = useI18n();
  const video = useRef(null);
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    if (active && !reduced) v.play().catch(() => {});
    else v.pause();
  }, [active, reduced]);
  const poster = mediaUrl(s.poster_url || s.image_url);
  const zoom = s.ken_burns && !reduced;
  return (
    <div className="absolute inset-0" aria-hidden={!active}>
      {s.slide_type === "video" && s.video_url && !reduced && (active || preload) ? (
        <video ref={video} data-testid={`hero-slide-video-${s.order}`} className="absolute inset-0 h-full w-full object-cover" poster={poster}
          muted loop playsInline autoPlay={active} preload={active ? "auto" : "metadata"} aria-label={tr(s, "title")}>
          <source src={mediaUrl(s.video_url)} type="video/mp4" />
          {s.video_webm_url && <source src={mediaUrl(s.video_webm_url)} type="video/webm" />}
        </video>
      ) : (
        <motion.img src={poster} alt={tr(s, "title")} className="absolute inset-0 h-full w-full object-cover object-center" loading={active ? "eager" : "lazy"}
          initial={{ scale: 1 }} animate={zoom && active ? { scale: 1.12 } : { scale: 1 }} transition={{ duration: zoom ? DURATION / 1000 + 2 : 0, ease: "linear" }} />
      )}
      <div className={`absolute inset-0 ${s.overlay === "heavy" ? "bg-canvas/75" : "bg-gradient-to-b from-canvas/70 via-canvas/35 to-canvas"}`} />
    </div>
  );
}

export default function HeroSlider({ slides }) {
  const { tr, isRtl, lang } = useI18n();
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = slides.length;
  const go = useCallback((d) => setI((x) => (x + d + n) % n), [n]);
  useEffect(() => {
    if (paused || reduced || n < 2) return undefined;
    const id = setTimeout(() => go(1), DURATION);
    return () => clearTimeout(id);
  }, [i, paused, reduced, n, go]);
  useEffect(() => { if (i >= n) setI(0); }, [n, i]);
  if (!n) return null;
  const s = slides[Math.min(i, n - 1)];
  const b = (en, ar) => (lang === "ar" ? ar : en);
  const Prev = isRtl ? ChevronRight : ChevronLeft;
  const Next = isRtl ? ChevronLeft : ChevronRight;
  return (
    <section data-testid="hero-slider" aria-roledescription="carousel" aria-label={b("Featured highlights", "أبرز المحتوى")} className="on-media relative flex min-h-[100svh] items-end overflow-hidden pb-16 pt-32">
      <AnimatePresence initial={false}>
        <motion.div key={s.id || i} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 1.1 }}>
          <Slide s={s} active reduced={reduced} />
        </motion.div>
      </AnimatePresence>
      {n > 1 && <div className="hidden"><Slide s={slides[(i + 1) % n]} active={false} reduced={reduced} preload /></div>}
      <div className="pattern-mashrabiya pointer-events-none absolute inset-0 opacity-25" aria-hidden="true" />
      <div className="container-lux relative w-full">
        <AnimatePresence mode="wait">
          <motion.div key={`t-${s.id || i}-${lang}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: reduced ? 0 : 0.7 }} aria-live="polite">
            <p className="eyebrow mb-5">{b("Landmark Future · Saudi Arabia", "لاندمارك فيوتشر · المملكة العربية السعودية")}</p>
            <h1 data-testid="hero-headline" className="h-display max-w-4xl text-4xl leading-[1.04] sm:text-5xl lg:text-7xl">{tr(s, "title")}</h1>
            {tr(s, "subtitle") && <p data-testid="hero-subheadline" className="mt-5 max-w-xl text-base text-fg/75 md:text-lg">{tr(s, "subtitle")}</p>}
            <div className="mt-8 flex flex-wrap gap-3">
              {s.cta_link && tr(s, "cta_label") && <Link to={s.cta_link} data-testid="hero-slide-cta" className="btn-cta">{tr(s, "cta_label")}</Link>}
              <Link to="/properties" data-testid="hero-cta-explore" className="btn-ghost">{b("Explore Properties", "استكشف العقارات")}</Link>
              <Link to="/developments" data-testid="hero-cta-discover" className="btn-ghost hidden sm:inline-flex">{b("Discover Our Projects", "اكتشف مشاريعنا")}</Link>
              <Link to="/investment" data-testid="hero-cta-invest" className="btn-ghost hidden md:inline-flex">{b("Invest With Us", "استثمر معنا")}</Link>
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="mt-10"><SearchBar /><NLSearch className="mt-3 max-w-3xl" testid="home-nl-search" /></div>
        {n > 1 && (
          <div className="mt-8 flex items-center gap-4" data-testid="hero-slider-controls">
            <button type="button" onClick={() => go(-1)} data-testid="hero-prev" aria-label={b("Previous slide", "الشريحة السابقة")} className="flex h-10 w-10 items-center justify-center rounded-full border border-fg/25 text-fg/80 transition-colors hover:border-cta hover:text-cta"><Prev className="h-4 w-4" /></button>
            <div className="flex gap-2" role="tablist">
              {slides.map((x, k) => (
                <button key={x.id || k} type="button" role="tab" aria-selected={k === i} onClick={() => setI(k)} data-testid={`hero-dot-${k}`} aria-label={`${b("Slide", "شريحة")} ${k + 1}`}
                  className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${k === i ? "w-10 bg-cta" : "w-4 bg-fg/35 hover:bg-fg/60"}`} />
              ))}
            </div>
            <button type="button" onClick={() => go(1)} data-testid="hero-next" aria-label={b("Next slide", "الشريحة التالية")} className="flex h-10 w-10 items-center justify-center rounded-full border border-fg/25 text-fg/80 transition-colors hover:border-cta hover:text-cta"><Next className="h-4 w-4" /></button>
            <button type="button" onClick={() => setPaused((p) => !p)} data-testid="hero-pause" aria-pressed={paused} aria-label={paused ? b("Play slideshow", "تشغيل العرض") : b("Pause slideshow", "إيقاف العرض")} className="flex h-10 w-10 items-center justify-center rounded-full border border-fg/25 text-fg/80 transition-colors hover:border-cta hover:text-cta">{paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}</button>
          </div>
        )}
      </div>
    </section>
  );
}
